import React, { useMemo } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const ComplianceGroupPercentBarChart = ({
  complianceStats,
  title = "Compliance %",
}) => {
  const { labels, percents, totals, yesCounts } = useMemo(() => {
    const map = new Map(); // description -> { yes, total }

    (complianceStats || []).forEach((r) => {
      const description = r?.Description ?? "Unknown";
      const yes = toNumber(r?.Yes);
      const total = toNumber(r?.Total);

      const cur = map.get(description) || { yes: 0, total: 0 };
      cur.yes += yes;
      cur.total += total;
      map.set(description, cur);
    });

    // Sort by percent desc, then by total desc (helps stabilize ties)
    const sorted = [...map.entries()].sort((a, b) => {
      const ap = a[1].total ? a[1].yes / a[1].total : 0;
      const bp = b[1].total ? b[1].yes / b[1].total : 0;
      if (bp !== ap) return bp - ap;
      return (b[1].total || 0) - (a[1].total || 0);
    });

    const labels = sorted.map(([d]) => d);
    const percents = sorted.map(([, v]) => (v.total ? (v.yes / v.total) * 100 : 0));
    const totals = sorted.map(([, v]) => v.total);
    const yesCounts = sorted.map(([, v]) => v.yes);

    return { labels, percents, totals, yesCounts };
  }, [complianceStats]);

  if (!complianceStats?.length) return null;

  const data = {
    labels,
    datasets: [
      {
        label: "Compliance %",
        data: percents,
        backgroundColor: "#DCEAF7", // requested color
        borderWidth: 0,
        borderRadius: 6,
        barThickness: 18,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y", // horizontal bars
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const i = ctx.dataIndex;
            const pct = ctx.parsed.x ?? 0;
            const yes = yesCounts[i] ?? 0;
            const total = totals[i] ?? 0;
            return ` ${pct.toFixed(1)}% (Yes: ${yes}, Total: ${total})`;
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: (v) => `${v}%`,
        },
        grid: { display: true },
      },
      y: {
        grid: { display: false },
        ticks: {
          // keep labels readable; tooltips show full text
          callback: function (value) {
            const label = this.getLabelForValue(value);
            if (!label) return "";
            return label.length > 50 ? `${label.slice(0, 50)}…` : label;
          },
        },
      },
    },
  };

  // Make the chart height scale with number of bars (prevents squishing)
  const height = Math.max(240, labels.length * 34);

  return (
    <Paper sx={{ p: 2, mt: 2, mb: 2, width: "97.5%" }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Box sx={{ height }}>
        <Bar data={data} options={options} />
      </Box>
    </Paper>
  );
};

export default ComplianceGroupPercentBarChart;
